import type {
	HandleCheckDistributorAttributionProps,
	HandleSetSelectedPosProps,
	HandleUpdateLinkedPosDataProps,
	PosParams,
	PosReducer,
} from './interfaces';
import type { DistributorsSourceParams_DEPRECATED } from 'pages/pos/Distributors/interfaces';
import { CustomerAccess } from 'stores/customer/interfaces';
import React, { createElement } from 'react';
import { Dispatch } from '@reduxjs/toolkit';
import {
	setCleanPos,
	setMyPos,
	setPosDistributors,
	setPosLoading,
	setSelectedPos,
	setUpdateDistributor,
	setHasDistributor,
	clearSelectedPosIndustryDistributors,
	setExistsBank,
} from './actions';
import { getStore } from 'stores/helpers';
import {
	checkBankExists,
	findMyPharmaciesNoHandler,
	findSelectedDistributors,
	posDistributorsCheck,
} from 'services/pos';
import { getBUs } from 'services/catalog';
import { handleSetCatalogMenuItems } from 'stores/catalog/middleware';
import { handleNavigateToFidelityPage } from 'stores/fidelity/middleware';
import { sellerViewErrorNotification } from './helpers';
import { insiderDispatcher } from 'services/insider';
import { mainCustomerPage, simulatedViewType } from 'stores/customer/helpers';
import { notify } from 'front-commons/ds';
import DistributorsError from 'containers/Dialogs/DistributorsError';
import { CheckSourceProps } from 'services/pos/interfaces';
import BUAttributionMissing from 'pages/pos/Distributors/dialogs/BUAttributionMissing';

export const handleSetMyPos = (myPos: PosParams[]): any => {
	return (dispatch: Dispatch) => {
		dispatch(setMyPos(myPos));
	};
};

export const handleSetHasDistributor = (hasDistributor: boolean): any => {
	return (dispatch: Dispatch) => {
		dispatch(setHasDistributor(hasDistributor));
	};
};

export const handleClearSelectedPos = (): any => {
	return (dispatch: Dispatch) => {
		dispatch(setSelectedPos({} as PosReducer['selectedPos']));
		dispatch(setPosLoading(false));
	};
};

export const handleSetUpdateDistributor = (source: DistributorsSourceParams_DEPRECATED): any => {
	return (dispatch: Dispatch) => {
		dispatch(setUpdateDistributor(source));
	};
};

export const handleClearSelectedPosIndustryDistributors = (): any => {
	return (dispatch: Dispatch) => {
		dispatch(clearSelectedPosIndustryDistributors());
	};
};

export const handleSetPosLoading = (loading: PosReducer['loading']): any => {
	return (dispatch: Dispatch) => {
		dispatch(setPosLoading(loading));
	};
};

export const handleSetPosDistributors = (distributors: Concrete<PosReducer['selectedPos']['distributors']>): any => {
	return (dispatch: Dispatch) => {
		dispatch(setPosDistributors(distributors));
	};
};

export const handleSetCleanPos = (): any => {
	return (dispatch: Dispatch) => {
		dispatch(setCleanPos());
	};
};

async function handleUpdateLinkedPosData({ posId, cnpj, handleIncrementToMyPos }: HandleUpdateLinkedPosDataProps) {
	const storedData = getStore().posReducer.myPos.find((pos) => pos.pointOfSaleId === posId);

	if (storedData) return storedData;
	if (!cnpj) return undefined;

	const { content } = await findMyPharmaciesNoHandler({ cnpj: CNPJ(cnpj).unmask });

	handleIncrementToMyPos?.(content.pointOfSaleList);

	const [data] = content.pointOfSaleList;

	return data;
}

export const handleCheckExistsBank = (posId: string): any => {
	return async (dispatch: Dispatch) => {
		try {
			const response = await checkBankExists({ posId });

			dispatch(setExistsBank(response.exists));
		} catch {
			dispatch(setExistsBank(undefined));
		}
	};
};

/**
 * Business Unities & Industries distribution validation
 */
export async function sourceValidation(posId: string) {
	try {
		const bus = await getBUs();
		const { businessUnities, industries } = await posDistributorsCheck({ posId });

		const sources: CheckSourceProps[] = [];
		const sourceNameFallback = 'Nome Desconhecido';

		let hasValidBU = false;
		let hasValidIndustry = false;

		businessUnities.forEach((item: CheckSourceProps) => {
			sources.push(item);
			if (item.status === 'VALID') hasValidBU = true;

			item.name ||= bus.find((bu) => bu.id === item.id)?.name || sourceNameFallback;

			return item;
		});

		industries.forEach((item: CheckSourceProps) => {
			sources.push(item);
			if (item.status === 'VALID') hasValidIndustry = true;

			item.name ||= sourceNameFallback;

			return item;
		});

		const invalidBUs = businessUnities
			.filter((bu) => bu.status !== 'VALID')
			.sort((a, b) => a.name!.localeCompare(b.name!));

		return {
			bus,
			sources,
			industries,
			invalidBUs,
			hasValidBU,
			businessUnities,
			hasValidIndustry,
		};
	} catch (error) {
		throw error;
	}
}

function handleCheckDistributorAttribution({
	posId,
	invalidBUs,
	businessUnities,
	hasValidIndustry,
	navigate,
	handleOpenDialog,
}: HandleCheckDistributorAttributionProps) {
	const cannotAttributeDistributors = simulatedViewType() === CustomerAccess.BASIC;

	const invalidType = (() => {
		if (!invalidBUs?.length) return 'NOT_INVALID';
		if (invalidBUs.length === businessUnities.length) return 'ALL_INVALID';

		return 'SOME_INVALID';
	})();

	if (invalidType !== 'NOT_INVALID') {
		const [{ id: buId }] = invalidBUs;

		handleOpenDialog?.({
			heading: {
				title:
					invalidType === 'ALL_INVALID'
						? 'Você precisa escolher seus distribuidores'
						: 'Finalize a configuração de Distribuidores',
				showCloseButton: true,
			},
			content: {
				children: cannotAttributeDistributors
					? 'Entre em contato com o administrador ou comprador desta farmácia e solicite para que ele selecione os distribuidores. Para consultar os preços é necessário selecionar ao menos um distribuidor por divisão.'
					: createElement(BUAttributionMissing, {
							invalidBUs,
							hasValidIndustry,
							allInvalid: invalidType === 'ALL_INVALID',
					  }),
			},
			footer: {
				primaryButton: {
					label: cannotAttributeDistributors ? 'Entendi' : 'Selecionar distribuidores',
					onClick() {
						if (cannotAttributeDistributors) return;

						navigate('/minhas-farmacias/distribuidores', { state: { buId, posId } });
					},
				},
				secondaryButton: {
					hide: cannotAttributeDistributors,
					label: 'Não acessar a loja agora',
				},
			},
		});

		return null;
	}

	return 'SUCCESS';
}

export const handleSetSelectedPos = ({
	cnpj,
	posId,
	location,
	isPartner,
	openSideBasket,
	isDistributorsPage,
	redirectUrl,
	navigate,
	handleFidelity,
	handleOpenDialog,
	handleGetBasketData,
	handleIncrementToMyPos,
}: HandleSetSelectedPosProps): any => {
	return async (dispatch: Dispatch) => {
		dispatch(handleSetHasDistributor(false));

		const { role, pointOfSaleCustomerId } =
			(await handleUpdateLinkedPosData({ posId, cnpj, handleIncrementToMyPos })) || {};

		try {
			dispatch(setPosLoading(posId));

			const { bus, sources, hasValidBU, industries, invalidBUs, businessUnities, hasValidIndustry } =
				await sourceValidation(posId);

			if (!sources.length) {
				notify.negative({ description: 'Nenhuma opção de atendimento encontrado para a farmácia.' });

				return null;
			}

			const attributionCheck = handleCheckDistributorAttribution({
				posId,
				invalidBUs,
				businessUnities,
				hasValidIndustry,
				navigate,
				handleOpenDialog: isPartner ? undefined : handleOpenDialog,
			});

			if (attributionCheck !== 'SUCCESS') return attributionCheck;

			const hasValidDistributor = hasValidBU || hasValidIndustry;

			if (sources.length > 0 && hasValidDistributor) {
				const BUsData = bus || (await getBUs(posId));
				const { pathname, state } = location;

				dispatch(handleSetHasDistributor(true));
				if (handleFidelity) dispatch(handleFidelity({ posId, role }));

				handleGetBasketData({ loading: 'full', initializeBefore: true, posId });

				const posData: PosReducer['selectedPos'] = { id: posId, role, businessUnities: BUsData, distributors: [] };

				if (!!businessUnities.length && hasValidBU) {
					const selectedBUsDistributors = await findSelectedDistributors({ posId });

					selectedBUsDistributors.forEach((bu) => {
						posData.distributors!.push({
							id: bu.businessUnitId,
							type: 'businessUnit',
							name: bu.businessUnitName || BUsData.find((BU) => BU.id === bu.businessUnitId)!.name,
							distributors: bu.distributors,
						});
					});
				}

				if (industries.length) {
					industries.forEach((industry) => {
						posData.distributors!.push({
							id: industry.id,
							name: industry.name!,
							type: 'industry',
							distributors: undefined,
						});
					});
				}

				await dispatch(setSelectedPos(posData));
				await dispatch(handleSetCatalogMenuItems(posData));

				if (role !== 'OWNER') {
					dispatch(setExistsBank(undefined));
				} else {
					await dispatch(handleCheckExistsBank(posId));
				}

				if (pointOfSaleCustomerId) insiderDispatcher.user({ uuid: pointOfSaleCustomerId });

				const startedFrom = redirectUrl || location.state?.startedFrom;

				if (startedFrom) {
					if (startedFrom === '/fidelidade') {
						dispatch(handleNavigateToFidelityPage(posId));
						navigate('/');
					} else {
						navigate(startedFrom, { state: null });
					}

					return null;
				}

				if ((isDistributorsPage && !!simulatedViewType()) || pathname.includes(mainCustomerPage())) {
					navigate('/', { state: openSideBasket ? { openSideBasket: true } : undefined });
				}

				if (pathname.includes('pedido-finalizado')) navigate('/', { replace: true });

				localStorage.setItem('searchModal', JSON.stringify(true));

				return null;
			}

			return null;
		} catch (error) {
			console.error(error);

			if (isPartner) throw error;

			if ((error as any)?.code === 403) {
				handleOpenDialog({
					heading: {
						title: 'Compra indisponível para essa farmácia no momento.',
						showCloseButton: true,
					},
					content: {
						description: React.createElement(DistributorsError),
					},
					footer: {
						primaryButton: {
							label: 'Ok, entendi',
						},
					},
					maxWidth: '620px',
				});

				return null;
			}

			if (!isDistributorsPage) {
				notify.negative({
					description:
						'Não foi possível executar essa ação. Por favor, entre em contato conosco para prosseguir com essa farmácia.',
				});

				return null;
			}

			notify.negative({
				description: simulatedViewType()
					? 'Esta farmácia está fora do painel comercial. Em caso de dúvida, entre em contato com a área de Efetividade.'
					: React.createElement(sellerViewErrorNotification),
			});

			return null;
		} finally {
			dispatch(setPosLoading(false));
		}
	};
};

export const handleSetBankExists = (exists: boolean): any => {
	return (dispatch: Dispatch) => {
		dispatch(setExistsBank(exists));
	};
};